import React, { useEffect, useState } from 'react';
import { adminEventService } from '../services';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { resolveMediaUrl } from '../services/api';
import { notifyError, notifyInfo, notifySuccess } from '../utils/toast';

const Events = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formError, setFormError] = useState('');
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        location: '',
        event_date: '',
        event_time: '',
        capacity: '',
        status: 'scheduled',
        featured_image: null,
    });

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            const response = await adminEventService.getAll();
            setEvents(response.data.data || []);
        } catch (error) {
            console.error('Error fetching events:', error);
            notifyError('Unable to load events.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, files } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'file' ? files?.[0] || null : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        try {
            if (editingId) {
                await adminEventService.update(editingId, formData);
                notifySuccess('Event updated successfully.');
            } else {
                await adminEventService.create(formData);
                notifySuccess('Event created successfully.');
            }
            fetchEvents();
            resetForm();
        } catch (error) {
            setFormError(error.response?.data?.message || 'Unable to save event.');
            console.error('Error saving event:', error);
            notifyError(error.response?.data?.message || 'Unable to save event.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure?')) {
            try {
                await adminEventService.delete(id);
                fetchEvents();
                notifySuccess('Event deleted successfully.');
            } catch (error) {
                console.error('Error deleting event:', error);
                notifyError('Unable to delete event.');
            }
        }
    };

    const handleEdit = (event) => {
        setFormData({
            title: event.title,
            description: event.description,
            location: event.location,
            event_date: event.event_date,
            event_time: event.event_time,
            capacity: event.capacity,
            status: event.status,
            featured_image: null,
        });
        setEditingId(event.id);
        setShowForm(true);
        notifyInfo('Event loaded for editing.');
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            location: '',
            event_date: '',
            event_time: '',
            capacity: '',
            status: 'scheduled',
            featured_image: null,
        });
        setEditingId(null);
        setFormError('');
        setShowForm(false);
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-4xl font-bold text-gray-800">Events</h1>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition flex items-center gap-2"
                >
                    <FaPlus /> Add Event
                </button>
            </div>

            {showForm && (
                <div className="bg-white p-8 rounded-lg shadow-lg mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6">{editingId ? 'Edit Event' : 'New Event'}</h2>
                    {formError && <div className="mb-4 rounded-lg bg-red-100 p-3 text-red-700">{formError}</div>}
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block font-semibold text-gray-700 mb-2">Title</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block font-semibold text-gray-700 mb-2">Location</label>
                            <input
                                type="text"
                                name="location"
                                value={formData.location}
                                onChange={handleInputChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block font-semibold text-gray-700 mb-2">Event Date</label>
                            <input
                                type="date"
                                name="event_date"
                                value={formData.event_date}
                                onChange={handleInputChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block font-semibold text-gray-700 mb-2">Event Time</label>
                            <input
                                type="time"
                                name="event_time"
                                value={formData.event_time}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block font-semibold text-gray-700 mb-2">Capacity</label>
                            <input
                                type="number"
                                name="capacity"
                                value={formData.capacity}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block font-semibold text-gray-700 mb-2">Featured Image</label>
                            <input
                                type="file"
                                name="featured_image"
                                accept="image/*"
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block font-semibold text-gray-700 mb-2">Status</label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            >
                                <option value="scheduled">Scheduled</option>
                                <option value="ongoing">Ongoing</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block font-semibold text-gray-700 mb-2">Description</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                required
                                rows="4"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            ></textarea>
                        </div>
                        <div className="md:col-span-2 flex gap-4">
                            <button
                                type="submit"
                                className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition"
                            >
                                Save
                            </button>
                            <button
                                type="button"
                                onClick={resetForm}
                                className="bg-gray-400 text-white px-6 py-2 rounded-lg hover:bg-gray-500 transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {loading ? (
                <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-emerald-600 text-white">
                                <tr>
                                    <th className="px-6 py-3 text-left">Title</th>
                                    <th className="px-6 py-3 text-left">Date</th>
                                    <th className="px-6 py-3 text-left">Location</th>
                                    <th className="px-6 py-3 text-left">Image</th>
                                    <th className="px-6 py-3 text-left">Status</th>
                                    <th className="px-6 py-3 text-left">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((event) => (
                                    <tr key={event.id} className="border-b hover:bg-gray-50">
                                        <td className="px-6 py-4">{event.title}</td>
                                        <td className="px-6 py-4">{new Date(event.event_date).toLocaleDateString()}</td>
                                        <td className="px-6 py-4">{event.location}</td>
                                        <td className="px-6 py-4">
                                            {event.featured_image && (
                                                <img
                                                    src={resolveMediaUrl(event.featured_image)}
                                                    alt={event.title}
                                                    className="h-10 w-10 rounded object-cover"
                                                />
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-3 py-1 rounded-full text-white text-xs font-semibold ${event.status === 'completed' ? 'bg-green-500' :
                                                    event.status === 'ongoing' ? 'bg-emerald-500' :
                                                        event.status === 'cancelled' ? 'bg-red-500' :
                                                            'bg-yellow-500'
                                                }`}>
                                                {event.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 flex gap-3">
                                            <button
                                                onClick={() => handleEdit(event)}
                                                className="text-emerald-600 hover:text-emerald-800"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(event.id)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Events;
